import { useCallback, useEffect, useReducer } from 'react'
import { rpc } from '../../api/rpc'
import type { ChatMessage, ChatState, ChatSummary, ChatTranscriptItem, PendingPromptInfo, QueuedMessage, ServerChatEvent, TodoItem } from '../../api/types'
import type { SubagentLiveInfo } from './feed'

export type { SubagentLiveInfo }

export interface LiveChat {
  loading: boolean
  chat: ChatSummary | null
  items: ChatTranscriptItem[]
  hasMore: boolean
  running: boolean
  /** id of the assistant message currently receiving deltas */
  streamingId: string | null
  prompt: PendingPromptInfo | null
  queue: QueuedMessage[]
  todos: TodoItem[]
  subagents: Record<string, SubagentLiveInfo>
  notice: string | null
  error: string | null
}

type Action =
  | { type: 'loaded'; state: ChatState }
  | { type: 'older'; items: ChatTranscriptItem[]; hasMore: boolean }
  | { type: 'transcript'; items: ChatTranscriptItem[]; hasMore: boolean }
  | { type: 'event'; event: ServerChatEvent }
  | { type: 'chat'; chat: ChatSummary }
  | { type: 'reset' }
  // The pending prompt was answered (here, in the Inbox, on Telegram) or expired.
  | { type: 'prompt-resolved'; promptId: string }

export const initialLive: LiveChat = {
  loading: true, chat: null, items: [], hasMore: false, running: false, streamingId: null,
  prompt: null, queue: [], todos: [], subagents: {}, notice: null, error: null
}

let liveSeq = 0

function upsertTool(items: ChatTranscriptItem[], tool: Extract<ChatTranscriptItem, { kind: 'tool' }>['tool']): ChatTranscriptItem[] {
  const i = items.findIndex(x => x.kind === 'tool' && x.tool.id === tool.id)
  if (i === -1) return [...items, { kind: 'tool', tool }]
  const next = items.slice()
  next[i] = { kind: 'tool', tool }
  return next
}

function appendDelta(s: LiveChat, field: 'text' | 'reasoning', delta: string): LiveChat {
  const last = s.items[s.items.length - 1]
  if (s.streamingId && last?.kind === 'message' && last.message.id === s.streamingId) {
    const message: ChatMessage = { ...last.message, [field]: (last.message[field] ?? '') + delta }
    return { ...s, items: [...s.items.slice(0, -1), { kind: 'message', message }] }
  }
  const message: ChatMessage = { id: `live-${++liveSeq}`, role: 'assistant', text: '', createdAt: Date.now(), [field]: delta }
  return { ...s, streamingId: message.id, items: [...s.items, { kind: 'message', message }] }
}

/** Applies one streamed core event; `done` triggers a refetch of the authoritative transcript. */
export function reduceLive(s: LiveChat, a: Action): LiveChat {
  switch (a.type) {
    case 'reset': return initialLive
    case 'loaded':
      return {
        ...s, loading: false, chat: a.state.chat, items: a.state.transcript.items, hasMore: a.state.transcript.hasMore,
        running: a.state.chat.running, prompt: a.state.pendingPrompt, queue: a.state.queue, todos: a.state.todos,
        // Runs already in flight when we subscribed: without them a card for a run
        // started before this client loaded would only appear at the next event.
        subagents: Object.fromEntries((a.state.subagents ?? []).map(sub => [sub.taskId, { ...sub, text: '', reasoning: '' }])),
        streamingId: null, error: null
      }
    case 'older': return { ...s, items: [...a.items, ...s.items], hasMore: a.hasMore }
    case 'transcript': return { ...s, items: a.items, hasMore: a.hasMore, streamingId: null }
    case 'prompt-resolved': return s.prompt?.promptId === a.promptId ? { ...s, prompt: null } : s
    case 'chat': return { ...s, chat: a.chat, running: a.chat.running || s.running }
    case 'event': {
      const e = a.event
      switch (e.type) {
        case 'user-message': return { ...s, items: [...s.items, { kind: 'message', message: e.message }], streamingId: null, error: null }
        case 'turn-started': return { ...s, running: true, error: null, notice: null }
        case 'step-start': return { ...s, streamingId: null }
        case 'text-delta': return appendDelta(s, 'text', e.delta)
        case 'reasoning-delta': return appendDelta(s, 'reasoning', e.delta)
        case 'tool-pending':
        case 'tool-start':
        case 'tool-result': return { ...s, items: upsertTool(s.items, e.call), streamingId: null }
        case 'subagent-event': {
          const current = s.subagents[e.taskId] ?? {
            taskId: e.taskId,
            subagentType: e.subagentType,
            state: 'running' as const,
            text: '',
            reasoning: '',
            toolCalls: [],
            callId: e.callId
          }
          const next: SubagentLiveInfo = {
            ...current,
            subagentType: e.subagentType ?? current.subagentType,
            callId: e.callId ?? current.callId,
            background: e.background !== undefined ? e.background : current.background
          }
          if (e.sub === 'start') {
            // A resumed task id starts a new run: its card must not keep the
            // previous run's text, result or tool list.
            next.state = 'running'
            next.text = ''
            next.reasoning = ''
            next.lastTool = undefined
            next.toolCalls = []
            next.result = undefined
          } else if (e.sub === 'delta') {
            if (e.text) next.text = (next.text || '') + e.text
            if (e.reasoning) next.reasoning = (next.reasoning || '') + e.reasoning
          } else if (e.tool) {
            next.lastTool = e.tool
            next.toolCalls = [...(next.toolCalls || []), e.tool]
          } else if (e.sub === 'done') {
            next.state = e.state ?? 'completed'
            if (e.result) next.result = e.result
            if (e.text && !next.text) next.text = e.text
          }
          return {
            ...s,
            subagents: {
              ...s.subagents,
              [e.taskId]: next
            }
          }
        }
        case 'prompt-request':
          return {
            ...s,
            prompt: {
              promptId: e.promptId, kind: e.kind, call: e.call, question: e.question, options: e.options,
              multiple: e.multiple, custom: e.custom, taskId: e.taskId, subagentType: e.subagentType
            }
          }
        case 'queue-updated': return { ...s, queue: e.queue }
        case 'todo-updated': return { ...s, todos: e.todos }
        case 'message-removed': return { ...s, items: s.items.filter(i => !(i.kind === 'message' && i.message.id === e.messageId)) }
        case 'notice': return { ...s, notice: e.text }
        case 'retry': return { ...s, notice: `retry ${e.attempt}/${e.maxAttempts}` }
        case 'error': return { ...s, error: e.message, running: false, prompt: null, streamingId: null }
        case 'done': return { ...s, running: false, prompt: null, streamingId: null, notice: null }
        default: return s
      }
    }
  }
}

export function useChat(chatId: string | undefined) {
  const [state, dispatch] = useReducer(reduceLive, initialLive)

  const load = useCallback(async () => {
    if (!chatId) return
    try {
      const st = await rpc.call<ChatState>('chats.subscribe', { chatId, limit: 200 })
      dispatch({ type: 'loaded', state: st })
    } catch (err) {
      dispatch({ type: 'event', event: { type: 'error', message: (err as Error).message } })
    }
  }, [chatId])

  const refetch = useCallback(async () => {
    if (!chatId) return
    const t = await rpc.call<{ items: ChatTranscriptItem[]; hasMore: boolean }>('chats.transcript', { chatId, limit: 200 })
    dispatch({ type: 'transcript', items: t.items, hasMore: t.hasMore })
  }, [chatId])

  useEffect(() => {
    dispatch({ type: 'reset' })
    if (!chatId) return
    void load()
    const offEvent = rpc.on('chat.event', (p: { chatId: string; event: ServerChatEvent }) => {
      if (p.chatId !== chatId) return
      dispatch({ type: 'event', event: p.event })
      if (p.event.type === 'done' || p.event.type === 'compacted') void refetch().catch(() => undefined)
    })
    const offChat = rpc.on('chat.updated', (c: ChatSummary) => { if (c.id === chatId) dispatch({ type: 'chat', chat: c }) })
    const offReconnect = rpc.on('rpc.reconnected', () => void load())
    const offResolved = rpc.on('approval.resolved', (a: { id: string; chatId: string | null }) => {
      if (a.chatId === chatId) dispatch({ type: 'prompt-resolved', promptId: a.id })
    })
    return () => {
      offEvent(); offChat(); offReconnect(); offResolved()
      void rpc.call('chats.unsubscribe', { chatId }).catch(() => undefined)
    }
  }, [chatId, load, refetch])

  const loadOlder = useCallback(async () => {
    const first = state.items[0]
    if (!chatId || !first) return
    const beforeId = first.kind === 'message' ? first.message.id : first.tool.id
    const t = await rpc.call<{ items: ChatTranscriptItem[]; hasMore: boolean }>('chats.transcript', { chatId, beforeId, limit: 100 })
    dispatch({ type: 'older', items: t.items, hasMore: t.hasMore })
  }, [chatId, state.items])

  const resolvePrompt = useCallback((promptId: string) => dispatch({ type: 'prompt-resolved', promptId }), [])

  return { state, loadOlder, reload: load, resolvePrompt }
}
